(function () {
  'use strict';

  var PLACEHOLDER_SELECTOR = '[data-spiderman-launch-placeholder]';
  var ROOT_ID = 'spiderman-launch';
  var SVG_NS = 'http://www.w3.org/2000/svg';
  var ADD_TO_CART_SELECTOR = [
    'button[type="submit"]',
    'input[type="submit"]',
    'button[name="add"]',
    '.product-form__submit',
    '.quick-add__submit'
  ].join(',');
  var OBSCURING_SELECTOR = 'quick-add-modal[open], cart-drawer.active, .drawer.active, details.menu-opening, .menu-drawer-container[open]';
  var MIN_MOBILE_WIDTH = 110;

  var root = null;
  var actor = null;
  var closeButton = null;
  var rig = null;
  var webPaths = [];
  var config = null;
  var activeShot = null;
  var clickRedirectTimer = null;
  var globalEventsBound = false;
  var placeholderObserver = null;
  var viewportFrame = null;
  var dismissedForPage = false;
  var reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  function ready(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
      return;
    }

    callback();
  }

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function toNumber(value, fallback) {
    var parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function viewportWidth() {
    var doc = document.documentElement;
    return (window.visualViewport && window.visualViewport.width) || window.innerWidth || doc.clientWidth || 0;
  }

  function viewportHeight() {
    var doc = document.documentElement;
    return (window.visualViewport && window.visualViewport.height) || window.innerHeight || doc.clientHeight || 0;
  }

  function isMobileViewport() {
    return viewportWidth() < 750;
  }

  function setLaunchMetrics(launch, nextConfig) {
    var desktopHeight = Math.round(nextConfig.desktopWidth * nextConfig.imageRatio);
    var mobileHeight = Math.round(nextConfig.mobileWidth * nextConfig.imageRatio);

    launch.style.setProperty('--spiderman-desktop-width', nextConfig.desktopWidth + 'px');
    launch.style.setProperty('--spiderman-mobile-width', nextConfig.mobileWidth + 'px');
    launch.style.setProperty('--spiderman-image-ratio', String(nextConfig.imageRatio));
    launch.style.setProperty('--spiderman-desktop-height', desktopHeight + 'px');
    launch.style.setProperty('--spiderman-mobile-height', mobileHeight + 'px');
    launch.style.setProperty('--spiderman-desktop-close-offset', Math.round(nextConfig.desktopWidth * 0.18) + 'px');
    launch.style.setProperty('--spiderman-mobile-close-offset', Math.round(nextConfig.mobileWidth * 0.16) + 'px');
  }

  function syncViewportMetrics() {
    if (!root) return;

    var visualViewport = window.visualViewport;
    var width = viewportWidth();
    var height = viewportHeight();
    var offsetTop = visualViewport && Number.isFinite(visualViewport.offsetTop) ? visualViewport.offsetTop : 0;
    var offsetLeft = visualViewport && Number.isFinite(visualViewport.offsetLeft) ? visualViewport.offsetLeft : 0;
    var layoutWidth = window.innerWidth || document.documentElement.clientWidth || width;
    var rightGap = Math.max(0, layoutWidth - (offsetLeft + width));

    root.style.setProperty('--spiderman-viewport-top', Math.round(Math.max(0, offsetTop)) + 'px');
    root.style.setProperty('--spiderman-viewport-height', Math.round(Math.max(1, height)) + 'px');
    root.style.setProperty('--spiderman-viewport-right-gap', Math.round(rightGap) + 'px');
  }

  function scheduleViewportMode() {
    if (viewportFrame) return;

    viewportFrame = window.requestAnimationFrame(function () {
      viewportFrame = null;
      updateViewportMode();
    });
  }

  function createElement(tagName, className) {
    var element = document.createElement(tagName);
    if (className) element.className = className;
    return element;
  }

  function createSvgElement(tagName, className) {
    var element = document.createElementNS(SVG_NS, tagName);
    if (className) element.setAttribute('class', className);
    return element;
  }

  function readConfig(placeholder) {
    var dataset = placeholder.dataset;
    var desktopWidth = Math.max(toNumber(dataset.desktopWidth, 150), 1);
    var mobileWidth = Math.max(toNumber(dataset.mobileWidth, MIN_MOBILE_WIDTH), MIN_MOBILE_WIDTH);
    var imageRatio = Math.max(toNumber(dataset.imageRatio, 1.7555), 0.1);

    return {
      themeEditor: dataset.themeEditor === 'true',
      collectionUrl: dataset.collectionUrl || '/collections/spiderman',
      imageSrc: dataset.imageSrc || '',
      imageSrcset: dataset.imageSrcset || '',
      imageWidth: Math.max(toNumber(dataset.imageWidth, 728), 1),
      imageHeight: Math.max(toNumber(dataset.imageHeight, 1278), 1),
      imageAlt: dataset.imageAlt || '',
      desktopWidth: desktopWidth,
      mobileWidth: mobileWidth,
      imageRatio: imageRatio
    };
  }

  function removeExistingLaunchNodes() {
    document.querySelectorAll('.spiderman-launch, #' + ROOT_ID).forEach(function (node) {
      node.remove();
    });
  }

  function consumePlaceholders() {
    var placeholders = Array.prototype.slice.call(document.querySelectorAll(PLACEHOLDER_SELECTOR));
    if (!placeholders.length) return null;

    var selected = placeholders[placeholders.length - 1];
    var nextConfig = readConfig(selected);
    placeholders.forEach(function (placeholder) {
      placeholder.remove();
    });

    return nextConfig;
  }

  function createWebField() {
    var svg = createSvgElement('svg', 'spiderman-launch__web-field');
    var defs = createSvgElement('defs');
    var filter = createSvgElement('filter');
    var blur = createSvgElement('feGaussianBlur');
    var merge = createSvgElement('feMerge');
    var mergeBlur = createSvgElement('feMergeNode');
    var mergeSource = createSvgElement('feMergeNode');
    var gradient = createSvgElement('linearGradient');
    var stopStart = createSvgElement('stop');
    var stopMiddle = createSvgElement('stop');
    var stopEnd = createSvgElement('stop');
    var main = createSvgElement('path', 'spiderman-launch__web-shot spiderman-launch__web-shot--main');
    var strandA = createSvgElement('path', 'spiderman-launch__web-shot spiderman-launch__web-shot--strand');
    var strandB = createSvgElement('path', 'spiderman-launch__web-shot spiderman-launch__web-shot--strand');

    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');

    filter.setAttribute('id', 'spiderman-web-glow');
    filter.setAttribute('x', '-30%');
    filter.setAttribute('y', '-30%');
    filter.setAttribute('width', '160%');
    filter.setAttribute('height', '160%');
    blur.setAttribute('in', 'SourceGraphic');
    blur.setAttribute('stdDeviation', '1.3');
    blur.setAttribute('result', 'blur');
    mergeBlur.setAttribute('in', 'blur');
    mergeSource.setAttribute('in', 'SourceGraphic');

    gradient.setAttribute('id', 'spiderman-web-gradient');
    gradient.setAttribute('x1', '0%');
    gradient.setAttribute('y1', '0%');
    gradient.setAttribute('x2', '100%');
    gradient.setAttribute('y2', '0%');
    stopStart.setAttribute('offset', '0%');
    stopStart.setAttribute('stop-color', '#ffffff');
    stopStart.setAttribute('stop-opacity', '0.12');
    stopMiddle.setAttribute('offset', '42%');
    stopMiddle.setAttribute('stop-color', '#ffffff');
    stopMiddle.setAttribute('stop-opacity', '0.92');
    stopEnd.setAttribute('offset', '100%');
    stopEnd.setAttribute('stop-color', '#dbe9ff');
    stopEnd.setAttribute('stop-opacity', '0.98');

    merge.append(mergeBlur, mergeSource);
    filter.append(blur, merge);
    gradient.append(stopStart, stopMiddle, stopEnd);
    defs.append(filter, gradient);
    svg.append(defs, main, strandA, strandB);

    webPaths = [main, strandA, strandB];
    return svg;
  }

  function createLaunchNode(nextConfig) {
    var launch = createElement('div', 'spiderman-launch');
    var link = createElement('a', 'spiderman-launch__actor');
    var dismissButton = createElement('button', 'spiderman-launch__close');
    var rigNode = createElement('span', 'spiderman-launch__rig');
    var shadow = createElement('span', 'spiderman-launch__shadow');
    var figure = createElement('span', 'spiderman-launch__figure');
    var image = createElement('img', 'spiderman-launch__image');

    launch.id = ROOT_ID;
    setLaunchMetrics(launch, nextConfig);
    if (nextConfig.themeEditor || isThemeEditorPreview()) launch.classList.add('spiderman-launch--theme-editor');
    if (isMobileViewport()) {
      launch.classList.add('spiderman-launch--mobile');
    }

    link.href = nextConfig.collectionUrl;
    link.setAttribute('aria-label', 'Shop Spider-Man merchandise');
    dismissButton.type = 'button';
    dismissButton.setAttribute('aria-label', 'Hide Spider-Man sticker');
    dismissButton.title = 'Hide Spider-Man sticker';

    image.src = nextConfig.imageSrc;
    if (nextConfig.imageSrcset) image.srcset = nextConfig.imageSrcset;
    image.sizes = '(max-width: 749px) ' + nextConfig.mobileWidth + 'px, ' + nextConfig.desktopWidth + 'px';
    image.width = nextConfig.imageWidth;
    image.height = nextConfig.imageHeight;
    image.loading = 'eager';
    image.alt = nextConfig.imageAlt;
    image.draggable = false;

    figure.append(image);
    rigNode.append(shadow, figure);
    link.append(rigNode);
    launch.append(createWebField(), link, dismissButton);

    return {
      launch: launch,
      link: link,
      closeButton: dismissButton,
      rigNode: rigNode
    };
  }

  function isThemeEditorPreview() {
    try {
      if (window.Shopify && window.Shopify.designMode) return true;
      if (document.documentElement.classList.contains('shopify-design-mode')) return true;
      if (hasShopifyPreviewBar()) return true;
      return window.self !== window.top;
    } catch (error) {
      return true;
    }
  }

  function hasShopifyPreviewBar() {
    return Boolean(
      document.querySelector(
        [
          '#preview-bar-iframe',
          'iframe[src*="preview_bar"]',
          'iframe[src*="preview-bar"]',
          'iframe[title*="preview" i]',
          '[id*="preview-bar" i]',
          '[class*="preview-bar" i]'
        ].join(',')
      )
    );
  }

  function neutralizeFixedContainingBlockAncestors(element) {
    var current = element.parentElement;

    while (current) {
      var style = window.getComputedStyle(current);
      if (style.transform && style.transform !== 'none') current.style.setProperty('transform', 'none', 'important');
      if (style.filter && style.filter !== 'none') current.style.setProperty('filter', 'none', 'important');
      if (style.perspective && style.perspective !== 'none') current.style.setProperty('perspective', 'none', 'important');
      if (style.contain && style.contain !== 'none') current.style.setProperty('contain', 'none', 'important');
      if (style.translate && style.translate !== 'none') current.style.setProperty('translate', 'none', 'important');
      if (style.willChange && /transform|filter|perspective|contain|translate/i.test(style.willChange)) {
        current.style.setProperty('will-change', 'auto', 'important');
      }
      current = current.parentElement;
    }
  }

  function mount(nextConfig) {
    removeExistingLaunchNodes();

    if (dismissedForPage) return;

    config = nextConfig;

    var created = createLaunchNode(config);
    root = created.launch;
    actor = created.link;
    closeButton = created.closeButton;
    rig = created.rigNode;
    document.body.appendChild(root);
    neutralizeFixedContainingBlockAncestors(root);

    bindRootEvents();
    bindGlobalEvents();
    updateViewportMode();
    toggleObscuredState();
  }

  function dismissLaunch() {
    dismissedForPage = true;
    clearActiveShot();
    window.clearTimeout(clickRedirectTimer);

    if (root) root.remove();

    root = null;
    actor = null;
    closeButton = null;
    rig = null;
    webPaths = [];
  }

  function updateViewportMode() {
    if (!root) return;
    syncViewportMetrics();
    var editor = config.themeEditor || isThemeEditorPreview();
    root.classList.toggle('spiderman-launch--theme-editor', editor);
    root.classList.toggle('spiderman-launch--mobile', isMobileViewport());
  }

  function isAddToCartButton(button) {
    if (!button || !(button instanceof Element)) return false;
    if (button.disabled || button.getAttribute('aria-disabled') === 'true') return false;
    if (button.closest('.shopify-payment-button')) return false;

    var form = button.form || button.closest('form');
    if (!form) return false;

    var action = form.getAttribute('action') || '';
    var type = form.getAttribute('data-type') || '';
    var buttonName = (button.getAttribute('name') || '').toLowerCase();
    var buttonText = (button.textContent || button.value || '').replace(/\s+/g, ' ').trim().toLowerCase();

    return (
      /\/cart\/add/i.test(action) ||
      type === 'add-to-cart-form' ||
      buttonName === 'add' ||
      button.classList.contains('product-form__submit') ||
      button.classList.contains('quick-add__submit') ||
      buttonText.indexOf('add to cart') !== -1
    );
  }

  function closestAddToCartButton(target) {
    if (!target || !(target instanceof Element)) return null;
    return target.closest(ADD_TO_CART_SELECTOR);
  }

  function point(x, y) {
    return Number(x).toFixed(2) + ' ' + Number(y).toFixed(2);
  }

  function actorPoint() {
    var rect = actor.getBoundingClientRect();
    return {
      x: rect.left + rect.width * 0.5,
      y: rect.top + rect.height * 0.22
    };
  }

  function buttonPoint(button) {
    var rect = button.getBoundingClientRect();
    return {
      x: rect.left + rect.width * 0.5,
      y: rect.top + rect.height * 0.5
    };
  }

  function webPathData(start, end) {
    var dx = end.x - start.x;
    var dy = end.y - start.y;
    var lift = clamp(Math.abs(dy) * 0.26, 24, 96);
    var bend = clamp(Math.abs(dx) * 0.18, 26, 120);
    var c1 = { x: start.x + dx * 0.34, y: start.y - lift };
    var c2 = { x: end.x - Math.sign(dx || 1) * bend, y: end.y + clamp(Math.abs(dy) * 0.08, 8, 34) };
    var strandOffset = clamp(Math.abs(dx) * 0.025, 4, 12);

    return [
      'M ' + point(start.x, start.y) + ' C ' + point(c1.x, c1.y) + ', ' + point(c2.x, c2.y) + ', ' + point(end.x, end.y),
      'M ' + point(start.x + 2, start.y - 1) + ' Q ' + point(start.x + dx * 0.58, start.y + dy * 0.35 - lift * 0.45) + ', ' + point(end.x + strandOffset, end.y - 7),
      'M ' + point(start.x - 2, start.y + 1) + ' Q ' + point(start.x + dx * 0.52, start.y + dy * 0.52 + lift * 0.1) + ', ' + point(end.x - strandOffset, end.y + 6)
    ];
  }

  function preparePath(path) {
    var length = 220;
    try {
      length = Math.max(path.getTotalLength(), 1);
    } catch (error) {
      length = 220;
    }

    path.style.strokeDasharray = length.toFixed(2);
    path.style.strokeDashoffset = length.toFixed(2);
    path.dataset.webLength = length.toFixed(2);
  }

  function clearActiveShot() {
    if (!activeShot) return;
    window.clearTimeout(activeShot.timeout);
    window.clearTimeout(activeShot.retractTimer);
    activeShot = null;
  }

  function resetWeb() {
    if (!root) return;
    root.classList.remove('is-web-active', 'is-web-retracting', 'is-web-success', 'is-web-error');
  }

  function triggerCatchAnimation() {
    if (!root || !rig || reducedMotionQuery.matches) return;

    if (window.gsap) {
      root.classList.add('is-gsap-active');
      window.gsap.killTweensOf(rig);
      window.gsap
        .timeline({
          onComplete: function () {
            if (root) root.classList.remove('is-gsap-active');
          }
        })
        .to(rig, { y: -12, rotate: -10, duration: 0.2, ease: 'power2.out' })
        .to(rig, { y: 3, rotate: 9, duration: 0.22, ease: 'sine.inOut' })
        .to(rig, { y: 0, rotate: 0, duration: 0.24, ease: 'elastic.out(1, 0.5)' });
      return;
    }

    root.classList.remove('is-catching');
    window.requestAnimationFrame(function () {
      if (!root) return;
      root.classList.add('is-catching');
      window.setTimeout(function () {
        if (root) root.classList.remove('is-catching');
      }, 720);
    });
  }

  function startWebFromButton(button) {
    if (!root || !isAddToCartButton(button)) return;

    clearActiveShot();
    resetWeb();

    var paths = webPathData(buttonPoint(button), actorPoint());
    webPaths.forEach(function (path, index) {
      path.setAttribute('d', paths[index]);
      preparePath(path);
    });

    root.classList.add('is-web-active');
    triggerCatchAnimation();

    activeShot = {
      timeout: window.setTimeout(function () {
        finishWebShot(true);
      }, reducedMotionQuery.matches ? 500 : 1100),
      retractTimer: null
    };

    window.requestAnimationFrame(function () {
      webPaths.forEach(function (path) {
        path.style.strokeDashoffset = '0';
      });
    });
  }

  function retractWeb() {
    if (!root || !activeShot) return;

    root.classList.add('is-web-retracting');
    webPaths.forEach(function (path) {
      path.style.strokeDashoffset = '-' + (path.dataset.webLength || '220');
    });

    activeShot.retractTimer = window.setTimeout(function () {
      resetWeb();
      clearActiveShot();
    }, reducedMotionQuery.matches ? 120 : 330);
  }

  function finishWebShot(success) {
    if (!root || !activeShot) return;

    window.clearTimeout(activeShot.timeout);
    root.classList.toggle('is-web-success', success);
    root.classList.toggle('is-web-error', !success);
    window.setTimeout(retractWeb, success ? 90 : 0);
  }

  function handleDocumentClick(event) {
    var button = closestAddToCartButton(event.target);
    if (button && isAddToCartButton(button)) startWebFromButton(button);
  }

  function handleDocumentSubmit(event) {
    var submitter = event.submitter || document.activeElement;
    var button = submitter && submitter.matches && submitter.matches(ADD_TO_CART_SELECTOR) ? submitter : event.target.querySelector(ADD_TO_CART_SELECTOR);
    if (button && isAddToCartButton(button)) startWebFromButton(button);
  }

  function subscribeToCartEvents() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      finishWebShot(true);
    });

    subscribe(PUB_SUB_EVENTS.cartError, function () {
      finishWebShot(false);
    });
  }

  function toggleObscuredState() {
    if (!root) return;
    root.classList.toggle('is-obscured', Boolean(document.querySelector(OBSCURING_SELECTOR)));
  }

  function bindRootEvents() {
    if (!root || !actor || !closeButton) return;

    closeButton.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      dismissLaunch();
    });

    actor.addEventListener('mouseenter', function () {
      root.classList.add('is-hovering');
    });

    actor.addEventListener('mouseleave', function () {
      root.classList.remove('is-hovering');
    });

    actor.addEventListener('focus', function () {
      root.classList.add('is-hovering');
    });

    actor.addEventListener('blur', function () {
      root.classList.remove('is-hovering');
    });

    actor.addEventListener('click', function (event) {
      event.preventDefault();
      window.clearTimeout(clickRedirectTimer);
      root.classList.add('is-launching');
      clickRedirectTimer = window.setTimeout(function () {
        window.location.href = config.collectionUrl;
      }, reducedMotionQuery.matches ? 60 : 420);
    });
  }

  function bindGlobalEvents() {
    if (globalEventsBound) return;
    globalEventsBound = true;

    document.addEventListener('click', handleDocumentClick, true);
    document.addEventListener('submit', handleDocumentSubmit, true);
    window.addEventListener('resize', scheduleViewportMode, { passive: true });
    window.addEventListener('orientationchange', scheduleViewportMode, { passive: true });
    window.addEventListener('pageshow', scheduleViewportMode, { passive: true });
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', scheduleViewportMode, { passive: true });
      window.visualViewport.addEventListener('scroll', scheduleViewportMode, { passive: true });
    }
    document.addEventListener('shopify:section:select', toggleObscuredState);
    document.addEventListener('shopify:section:deselect', toggleObscuredState);
    subscribeToCartEvents();
  }

  function observePlaceholders() {
    if (!('MutationObserver' in window) || placeholderObserver) return;

    placeholderObserver = new MutationObserver(function (mutations) {
      var shouldMount = false;
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (!(node instanceof Element)) return;
          if (node.matches(PLACEHOLDER_SELECTOR) || node.querySelector(PLACEHOLDER_SELECTOR)) {
            shouldMount = true;
          }
        });
      });

      if (shouldMount) {
        var nextConfig = consumePlaceholders();
        if (nextConfig && !dismissedForPage) mount(nextConfig);
      }
      scheduleViewportMode();
      toggleObscuredState();
    });

    placeholderObserver.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  function init() {
    var nextConfig = consumePlaceholders();
    if (nextConfig && !dismissedForPage) mount(nextConfig);
    observePlaceholders();
  }

  ready(init);
})();
